import { Field, ErrorMessage, FormikProps } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';

interface QuestionBasicFieldsProps {
  formik: FormikProps<QuestionFormValues>;
  pickems: Array<{ pickem_id: string; title_en?: string; title_fr?: string }>;
  isEditable: boolean;
  isNewQuestion: boolean;
}

export function QuestionBasicFields({
  formik,
  pickems,
  isEditable,
  isNewQuestion: _isNewQuestion,
}: QuestionBasicFieldsProps) {
  const { errors, touched, values, setFieldValue } = formik;

  // Check if question type is set by grading type (readonly mode)
  const gradingType = values.question_metadata?.grading_type;
  const isQuestionTypeLockedByGrading = Boolean(gradingType);

  const answerCount: 2 | 4 = values.question_metadata?.config?.option_count === 4 ? 4 : 2;

  const setAnswerCount = (count: 2 | 4) => {
    const currentMeta = values.question_metadata ?? {};
    const currentConfig = currentMeta.config ?? {};

    setFieldValue('question_metadata', {
      ...currentMeta,
      config: {
        ...currentConfig,
        option_count: count,
      },
    });
  };

  const handleQuestionTypeChange = (value: string) => {
    setFieldValue('question_type', value);

    // Tiebreaker has no options; clear option_count as it doesn't apply
    if (value === 'tiebreaker') {
      const currentMeta = values.question_metadata ?? {};
      const currentConfig = currentMeta.config ?? {};

      setFieldValue('question_metadata', {
        ...currentMeta,
        config: {
          ...currentConfig,
          option_count: undefined,
        },
      });
    } else {
      // Ensure there's always a sensible default when switching back
      // (keeps behaviour stable and predictable)
      setAnswerCount(answerCount ?? 2);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Pick'em dropdown - full width */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="pickem_id">Pickem</Label>
        <Select
          value={values.pickem_id}
          onValueChange={value => setFieldValue('pickem_id', value)}
          disabled={!isEditable}
        >
          <SelectTrigger
            data-test-id="question-pickem_id-select"
            className={errors.pickem_id && touched.pickem_id ? 'border-red-500' : ''}
          >
            <SelectValue placeholder="Select a pickem" />
          </SelectTrigger>
          <SelectContent>
            {pickems.map(pickem => (
              <SelectItem key={pickem.pickem_id} value={pickem.pickem_id.toString()}>
                {pickem.title_en || pickem.title_fr || `Pickem #${pickem.pickem_id}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ErrorMessage name="pickem_id">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>

      {/* Question Type dropdown - full width */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="question_type">Question Type</Label>
        <Select
          value={values.question_type}
          onValueChange={handleQuestionTypeChange}
          disabled={!isEditable || isQuestionTypeLockedByGrading}
        >
          <SelectTrigger
            data-test-id="question-question_type-select"
            className={errors.question_type && touched.question_type ? 'border-red-500' : ''}
          >
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="team">Team Selection</SelectItem>
            <SelectItem value="player">Player Selection</SelectItem>
            <SelectItem value="prop">Yes/No Proposition</SelectItem>
            <SelectItem value="tiebreaker">Tiebreaker</SelectItem>
          </SelectContent>
        </Select>
        <ErrorMessage name="question_type">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
        {isQuestionTypeLockedByGrading && (
          <p className="text-xs text-muted-foreground">
            Question type is automatically set based on the grading configuration
          </p>
        )}
      </div>

      {/* Number of answers dropdown - full width (not for tiebreakers) */}
      {values.question_type !== 'tiebreaker' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="answer_count">Number of answers</Label>
          <Select
            value={String(answerCount)}
            onValueChange={v => setAnswerCount(v === '4' ? 4 : 2)}
            disabled={!isEditable}
          >
            <SelectTrigger data-test-id="question-answer_count-select">
              <SelectValue placeholder="Select count" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 answers</SelectItem>
              <SelectItem value="4">4 answers</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Points input - full width */}
      {values.question_type !== 'tiebreaker' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="points">Points</Label>
          <Field
            as={Input}
            type="number"
            id="points"
            name="points"
            data-test-id="question-points"
            placeholder="Points for correct answer"
            disabled={!isEditable}
            className={errors.points && touched.points ? 'border-red-500' : ''}
          />
          <ErrorMessage name="points">
            {msg => <span className="text-sm text-red-500">{msg}</span>}
          </ErrorMessage>
        </div>
      )}

      {/* Correct Value input - full width */}
      {values.question_type === 'tiebreaker' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="correct_value">Correct Value</Label>
          <Field
            as={Input}
            type="number"
            step="0.01"
            id="correct_value"
            name="correct_value"
            data-test-id="question-correct_value"
            placeholder="Enter correct tiebreaker value"
            disabled={!isEditable}
            className={errors.correct_value && touched.correct_value ? 'border-red-500' : ''}
          />
          <ErrorMessage name="correct_value">
            {msg => <span className="text-sm text-red-500">{msg}</span>}
          </ErrorMessage>
        </div>
      )}
    </div>
  );
}
